import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { SubmitButton } from '../common/form/SubmitButton';
import { Input } from '../common/form/Input';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { DatePicker } from '../common/form/DatePicker';
import { QuestionnaireAutocomplete } from '../common/form/QuestionnaireAutocomplete';
import { trainingVersionListActions } from '../../store/entities/trainingVersionList';
import { QcsTextField } from '../common/basic/QcsTextField';
import { TrainingSlideLanguages } from './TrainingSlideLanguages';
import styled from '@emotion/styled';
import { selectIdentity } from '../../store/entities/identity';
import { QcsBox } from '../common/basic/QcsBox';
import QcsAlert from '../common/basic/QcsAlert';
import { isDateDisabled } from '../../utils/date';
import { getLangNameObj } from '../../utils/format';
import { TrainingVersion } from '../../models/training';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { getTraining, selectTraining, selectTrainingState } from '../../store/entities/training';
import { getTrainingVersion, selectTrainingVersion, selectTrainingVersionState, trainingVersionActions } from '../../store/entities/trainingVersion';
import { TrainingVersionDto } from '@api/models/training/TrainingVersionDto';
import { TrainingRunButton } from '../trainingRun/TrainingRunButton';
import { trainingVersionService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';

const ButtonsContainer = styled.div(({ theme }) => ({
  display: 'flex',
  gap: '0.5rem',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: { flexDirection: 'row' },
}));

const Alert = memo(({ text }: { text: string }) => {
  return (
    <QcsBox sx={{ pb: '1rem' }}>
      <QcsAlert severity="warning">{text}</QcsAlert>
    </QcsBox>
  );
});

export const TrainingVersionDetailData: FC = () => {
  const { trainingId, trainingVersionId } = useParams();
  const { t, i18n } = useTranslation();
  const { navigate, getPath, getBackPath } = useNavigation();
  const identity = useAppSelector(selectIdentity);
  const training = useAppSelector(selectTraining);
  const trainingVersion = useAppSelector(selectTrainingVersion);
  const dispatch = useAppDispatch();

  const trainingDetailPath = getPath(PathKey.TrainingDetail, { trainingId: trainingId! });
  const backTo = getBackPath || trainingDetailPath;

  useEffect(() => {
    if (trainingVersionId === 'new') {
      dispatch(trainingVersionActions.default());
    } else {
      dispatch(getTrainingVersion(trainingVersionId!));
    }
    dispatch(getTraining(trainingId!));
  }, [trainingId, dispatch, trainingVersionId]);

  const handleSubmit = async (data: TrainingVersionDto) => {
    const data2: TrainingVersion = {
      ...data,
      validTo: undefined,
    };

    if (trainingVersionId === 'new') {
      await trainingVersionService.create(data2, { trainingId: trainingId! });
    } else {
      await trainingVersionService.update(trainingVersionId!, data2);
    }

    dispatch(trainingVersionListActions.reload(true));
    navigate(PathKey.TrainingDetail, { trainingId: trainingId! });
  };

  return (
    <>
      <DetailHeader title="training.version" backTo={backTo} />
      <ShowResourceContent fetchStateSelector={[selectTrainingVersionState, selectTrainingState]}>
        <FormikDebounce<TrainingVersion>
          initialValues={trainingVersion}
          validationSchema={
            trainingVersion.editable
              ? Yup.object().shape({
                  validFrom: trainingVersion.first ? validations.dateRequired(t) : validations.stringDateNotPastOrTodayRequired(t),
                  duration: validations.intPositive(t),
                })
              : undefined
          }
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              {!values.questionnaire ? (
                <Alert text={t('training.noTestJoined')} />
              ) : (
                values.questionnaire.missingLanguageVariant && <Alert text={t('training.missingLanguageVariant')} />
              )}

              <FormContainer>
                <QcsTextField value={getLangNameObj(i18n, training)} label={t('training.detailTitle')} disabled />

                <DatePicker
                  name="validFrom"
                  label={t('training.validFrom')}
                  disablePast
                  disableHighlightToday
                  shouldDisableDate={isDateDisabled}
                  required
                  disabled={!trainingVersion.editable || !!trainingVersion.first}
                />
                <DatePicker name="validTo" label={t('training.validTo')} disabled />

                <QuestionnaireAutocomplete name="questionnaire" label={t('training.questionnaire')} disabled={!trainingVersion.editable} />

                <Input name="duration" label={t('training.duration')} type="number" disabled={!trainingVersion.editable} required />

                <Input name="note" label={t('training.versionNote')} maxLength={250} multiline={true} rows={4} disabled={!trainingVersion.editable} />

                <ShowError resource={[resources.createTrainingVersion, resources.updateTrainingVersion]} />
                <ButtonsContainer>
                  {trainingVersionId !== 'new' && (
                    <TrainingRunButton
                      workers={{
                        id: identity.id,
                        employeeId: '',
                        name: identity.name ?? '',
                        lastName: '',
                        activities: [],
                        workplaces: [],
                        requiredTrainings: [trainingVersion],
                      }}
                      variant="outlined"
                      disabled={(trainingVersion.languageVariants?.length ?? 0) < 1}
                      isPlayground
                    >
                      {t('training.runPlayground')}
                    </TrainingRunButton>
                  )}
                  <SubmitButton disabled={!trainingVersion.editable} />
                </ButtonsContainer>
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
      </ShowResourceContent>

      {trainingVersionId !== 'new' && <TrainingSlideLanguages />}
    </>
  );
};
